import api from "@/lib/api/axios";

export interface Location {
  longitude: number;
  latitude: number;
}

// Updated User interface based on API documentation
export interface User {
  id: string;
  username: string;
  display_name: string;
  email: string;
  first_name: string;
  last_name: string;
  bio: string;
  website?: string;
  gender?: "male" | "female" | "non_binary" | "prefer_not_to_say" | "other";
  emails_of_related_user?: string[];
  emergency_contacts?: string[];
  location_name?: string;
  date_of_birth?: string; // YYYY-MM-DD format
  avatar_url?: string;
  is_staff: boolean;
  is_verified: boolean;
  joined_at: string;
  last_login: string;
  post_count: number;
  communities: unknown[];
  location?: Location;
  notification_settings?: {
    email_digest?: boolean;
    push_notifications?: boolean;
    mention_notifications?: boolean;
    reply_notifications?: boolean;
  };
  preferences?: {
    theme?: "light" | "dark";
    language?: string;
  };
  privacy_settings?: {
    profile_visibility?: "public" | "members" | "private";
    show_email?: boolean;
    show_location?: boolean;
    allow_friend_requests?: boolean;
    email_notifications?: boolean;
    push_notifications?: boolean;
  };
}

export interface PresignedUrlResponse {
  presigned_url: string;
  file_url: string;
}

export interface AvatarResponse {
  avatar_url: string;
}

export interface PaginatedUserResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: User[];
}

export interface UserListParams {
  page?: number;
  ordering?: string;
  search?: string;
}

export interface CreateUserData {
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
  bio?: string;
  website?: string;
  gender?: "male" | "female" | "non_binary" | "prefer_not_to_say" | "other";
  emails_of_related_user?: string[];
  emergency_contacts?: string[];
  location_name?: string;
  date_of_birth?: string;
  location?: Location;
  password: string;
  notification_settings?: {
    email_digest?: boolean;
    push_notifications?: boolean;
    mention_notifications?: boolean;
    reply_notifications?: boolean;
  };
  preferences?: {
    theme?: "light" | "dark";
    language?: string;
  };
  privacy_settings?: {
    profile_visibility?: "public" | "members" | "private";
    show_email?: boolean;
    show_location?: boolean;
    allow_friend_requests?: boolean;
    email_notifications?: boolean;
    push_notifications?: boolean;
  };
}

export interface UpdateUserData {
  first_name?: string;
  last_name?: string;
  bio?: string;
  website?: string;
  gender?: "male" | "female" | "non_binary" | "prefer_not_to_say" | "other";
  emails_of_related_user?: string[];
  emergency_contacts?: string[];
  location_name?: string;
  date_of_birth?: string;
  location?: Location;
  notification_settings?: {
    email_digest?: boolean;
    push_notifications?: boolean;
    mention_notifications?: boolean;
    reply_notifications?: boolean;
  };
  preferences?: {
    theme?: "light" | "dark";
    language?: string;
  };
  privacy_settings?: {
    profile_visibility?: "public" | "members" | "private";
    show_email?: boolean;
    show_location?: boolean;
    allow_friend_requests?: boolean;
    email_notifications?: boolean;
    push_notifications?: boolean;
  };
}

export type PatchUserData = Partial<UpdateUserData>;

export interface UserRatingData {
  rating: number;
}

export const usersApi = {
  /**
   * Get list of users with pagination, ordering, and search
   */
  getUsers: async (params?: UserListParams): Promise<PaginatedUserResponse> => {
    const response = await api.get<PaginatedUserResponse>("/api/users/", {
      params,
    });
    return response.data;
  },

  /**
   * Get a single user profile by ID
   */
  getUserProfile: async (id: string): Promise<User> => {
    const response = await api.get<User>(`/api/users/profile/${id}/`);
    return response.data;
  },

  /**
   * Get a single user by ID (alternative endpoint)
   */
  getUser: async (id: string): Promise<User> => {
    const response = await api.get<User>(`/api/users/${id}/`);
    return response.data;
  },

  /**
   * Create a new user account
   * Note: In most cases, user creation is handled by authentication processes
   * rather than direct API calls
   */
  createUser: async (data: CreateUserData): Promise<User> => {
    const response = await api.post<User>("/api/users/", data);
    return response.data;
  },

  /**
   * Update a user (full update)
   */
  updateUser: async (id: string, data: UpdateUserData): Promise<User> => {
    const response = await api.put<User>(`/api/users/${id}/`, data);
    return response.data;
  },

  /**
   * Partially update a user
   */
  patchUser: async (id: string, data: PatchUserData): Promise<User> => {
    const response = await api.patch<User>(`/api/users/${id}/`, data);
    return response.data;
  },

  /**
   * Delete a user
   * Note: Deleting users is often a sensitive operation with additional
   * security checks or soft-delete patterns
   */
  deleteUser: async (id: string): Promise<void> => {
    await api.delete(`/api/users/${id}/`);
  },

  /**
   * Get the current user's profile
   * Uses the /api/auth/me/ endpoint which is more appropriate for current user
   */
  getCurrentUser: async (): Promise<User> => {
    const response = await api.get<User>("/api/auth/me/");
    return response.data;
  },

  /**
   * Get user's communities
   */
  getUserCommunities: async (id: string): Promise<unknown[]> => {
    const response = await api.get(`/api/users/profile/${id}/communities/`);
    return response.data;
  },

  /**
   * Get user's favors
   */
  getUserFavors: async (id: string): Promise<unknown[]> => {
    const response = await api.get(`/api/users/profile/${id}/favors/`);
    return response.data;
  },

  /**
   * Rate a user
   */
  rateUser: async (id: string, data: UserRatingData): Promise<void> => {
    await api.post(`/api/users/profile/${id}/rate/`, data);
  },

  /**
   * Get user's avatar upload URL
   */
  getAvatarUploadUrl: async (
    id: string,
    contentType: string,
  ): Promise<PresignedUrlResponse> => {
    const response = await api.post<PresignedUrlResponse>(
      `/api/users/${id}/upload_avatar/`,
      {
        content_type: contentType,
      },
    );
    return response.data;
  },

  /**
   * Confirm avatar upload
   */
  confirmAvatarUpload: async (id: string): Promise<AvatarResponse> => {
    const response = await api.post<AvatarResponse>(
      `/api/users/${id}/confirm_avatar/`,
    );
    return response.data;
  },

  /**
   * Upload file to presigned URL
   */
  uploadToPresignedUrl: async (url: string, file: File): Promise<boolean> => {
    const response = await fetch(url, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": file.type,
      },
    });
    return response.ok;
  },
};
